import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { apiBaseUrl } from '../../util/api';

// ================ Action types ================ //

export const UPLOAD_GALLERY_IMAGES_REQUEST = 'app/GalleryPage/UPLOAD_GALLERY_IMAGES_REQUEST';
export const UPLOAD_GALLERY_IMAGES_SUCCESS = 'app/GalleryPage/UPLOAD_GALLERY_IMAGES_SUCCESS';
export const UPLOAD_GALLERY_IMAGES_ERROR = 'app/GalleryPage/UPLOAD_GALLERY_IMAGES_ERROR';

export const CREATE_GALLERY_LISTING_REQUEST = 'app/GalleryPage/CREATE_GALLERY_LISTING_REQUEST';
export const CREATE_GALLERY_LISTING_SUCCESS = 'app/GalleryPage/CREATE_GALLERY_LISTING_SUCCESS';
export const CREATE_GALLERY_LISTING_ERROR = 'app/GalleryPage/CREATE_GALLERY_LISTING_ERROR';

export const RESET_GALLERY_STATE = 'app/GalleryPage/RESET_GALLERY_STATE';

// ================ Reducer ================ //

const initialState = {
    uploadInProgress: false,
    uploadError: null,
    uploadedImages: [],
    createListingInProgress: false,
    createListingError: null,
    createdListing: null,
};

export default function reducer(state = initialState, action = {}) {
    const { type, payload } = action;
    switch (type) {
        case UPLOAD_GALLERY_IMAGES_REQUEST:
            return {
                ...state,
                uploadInProgress: true,
                uploadError: null,
            };
        case UPLOAD_GALLERY_IMAGES_SUCCESS:
            return {
                ...state,
                uploadInProgress: false,
                uploadedImages: [...state.uploadedImages, ...payload],
            };
        case UPLOAD_GALLERY_IMAGES_ERROR:
            return {
                ...state,
                uploadInProgress: false,
                uploadError: payload,
            };
        case CREATE_GALLERY_LISTING_REQUEST:
            return {
                ...state,
                createListingInProgress: true,
                createListingError: null,
            };
        case CREATE_GALLERY_LISTING_SUCCESS:
            return {
                ...state,
                createListingInProgress: false,
                createdListing: payload,
            };
        case CREATE_GALLERY_LISTING_ERROR:
            return {
                ...state,
                createListingInProgress: false,
                createListingError: payload,
            };
        case RESET_GALLERY_STATE:
            return initialState;
        default:
            return state;
    }
}

// ================ Action creators ================ //

export const uploadGalleryImagesRequest = () => ({ type: UPLOAD_GALLERY_IMAGES_REQUEST });
export const uploadGalleryImagesSuccess = images => ({
    type: UPLOAD_GALLERY_IMAGES_SUCCESS,
    payload: images,
});
export const uploadGalleryImagesError = error => ({
    type: UPLOAD_GALLERY_IMAGES_ERROR,
    payload: error,
    error: true,
});

export const createGalleryListingRequest = () => ({ type: CREATE_GALLERY_LISTING_REQUEST });
export const createGalleryListingSuccess = listing => ({
    type: CREATE_GALLERY_LISTING_SUCCESS,
    payload: listing,
});
export const createGalleryListingError = error => ({
    type: CREATE_GALLERY_LISTING_ERROR,
    payload: error,
    error: true,
});

export const resetGalleryState = () => ({ type: RESET_GALLERY_STATE });

// ================ Thunks ================ //

export const uploadGalleryImages = files => (dispatch, getState, sdk) => {
    dispatch(uploadGalleryImagesRequest());

    // Create FormData to send files to backend
    const formData = new FormData();
    files.forEach((file, index) => {
        formData.append('images', file);
    });

    const url = `${apiBaseUrl()}/api/gallery/upload`;

    return fetch(url, {
        method: 'POST',
        body: formData,
        credentials: 'include',
    })
        .then(response => {
            if (!response.ok) {
                return response.json().then(err => {
                    throw new Error(err.message || 'Upload failed');
                }).catch(err => {
                    throw new Error(err.message || `Upload failed with status: ${response.status}`);
                });
            }
            return response.json();
        })
        .then(data => {
            dispatch(uploadGalleryImagesSuccess(data.images));
            return data; // Return the data so the component can access it
        })
        .catch(e => {
            log.error(e, 'gallery-upload-failed');
            dispatch(uploadGalleryImagesError(storableError(e)));
            throw e;
        });
};

export const createGalleryListing = data => (dispatch, getState, sdk) => {
    dispatch(createGalleryListingRequest());

    const url = `${apiBaseUrl()}/api/gallery/create-listing`;

    return fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
        credentials: 'include',
    })
        .then(response => {
            if (!response.ok) {
                return response.json().then(err => {
                    throw new Error(err.message || 'Listing creation failed');
                }).catch(() => {
                    throw new Error(`Listing creation failed with status: ${response.status}`);
                });
            }
            return response.json();
        })
        .then(data => {
            dispatch(createGalleryListingSuccess(data.listing));
            return data;
        })
        .catch(e => {
            log.error(e, 'gallery-listing-creation-failed');
            dispatch(createGalleryListingError(storableError(e)));
            throw e;
        });
};
